import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { getActions, updateActionStatus } from '@/api/actions';
import { getUserInfo } from '@/api/auth';
import { format } from 'date-fns';
import { HashtagSearch } from '@/components/HashtagSearch';
import { useToast } from '@/hooks/useToast';
import debounce from 'lodash.debounce';
import { Meeting } from '@/types/Meeting';
import { CollaborationIndicator } from '@/components/CollaborationIndicator';

const renderHashtagText = (text: string, hashtags: string[]) => {
  const parts = text.split(/(#\w+)/g);
  return (
    <span>
      {parts.map((part, index) => {
        const tag = part.startsWith('#') ? part.slice(1) : null;
        if (tag && hashtags.includes(tag)) {
          return (
            <span key={index} className="inline-block bg-primary/10 text-primary px-2 py-0.5 rounded-full text-sm">
              {part}
            </span>
          );
        }
        return part;
      })}
    </span>
  );
};

export function Actions() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const navigate = useNavigate();
  const { toast } = useToast();
  const observerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const ageRange = searchParams.get('ageRange') || '';
  const period = searchParams.get('period') || '';
  const includeSharedParam = searchParams.get('includeShared');
  const [includeShared, setIncludeShared] = useState(includeSharedParam !== 'false');

  // Get current user's email
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userInfo = await getUserInfo();
        setCurrentUserEmail(userInfo.email);
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    
    fetchUserInfo();
  }, []);

  const fetchActions = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);

    try {
      const response = await getActions(page, 25, {
        search,
        showCompleted,
        ageRange,
        period,
        includeShared
      });

      setMeetings((prevMeetings) => {
        const uniqueMeetings = [
          ...new Map([...prevMeetings, ...response.meetings].map((m) => [m.id, m])).values(),
        ];
        return uniqueMeetings;
      });
      setHasMore(response.meetings.length === 25);
    } catch (error) {
      console.error('Error fetching actions:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load actions"
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, search, showCompleted, ageRange, period, includeShared]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.5,
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasMore, isLoading]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearch(value);
      setMeetings([]);
      setPage(1);
      setHasMore(true);
    }, 300),
    [] // Empty dependency array ensures stable reference
  );

  const handleActionToggle = async (meetingId: string, actionId: string, completed: boolean) => {
    try {
      await updateActionStatus(meetingId, actionId, completed);
      setMeetings(currentMeetings => 
        currentMeetings.map(meeting => ({
          ...meeting,
          actions: meeting.actions.map(action =>
            action.id === actionId ? { ...action, completed } : action
          )
        }))
      );
    } catch (error) {
      console.error('Error updating action:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update action status"
      });
    }
  };

  const allHashtags = Array.from(new Set(
    meetings.flatMap(meeting => 
      meeting.actions.flatMap(action => action.hashtags)
    ).filter((hashtag): hashtag is string => !!hashtag)
  ));

  const clearAgeFilter = () => {
    searchParams.delete('ageRange');
    setSearchParams(searchParams);
    setMeetings([]);
    setPage(1);
    setHasMore(true);
  };

  return (
    <div className="space-y-8">
      {ageRange && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm">
            Showing actions: {ageRange}
          </Badge>
          <button
            onClick={clearAgeFilter}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear age filter"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div className="w-full md:w-96">
          <HashtagSearch
            value={search}
            onChange={debouncedSearch}
            allHashtags={allHashtags}
          />
        </div>
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-3">
            <Switch
              id="include-shared"
              checked={includeShared}
              onCheckedChange={(checked) => {
                setIncludeShared(checked);
                setMeetings([]);
                setPage(1);
                setHasMore(true);
              }}
            />
            <Label htmlFor="include-shared">Include shared</Label>
          </div>
          <div className="flex items-center space-x-3">
            <Switch
              id="show-completed"
              checked={showCompleted}
              onCheckedChange={(checked) => {
                setShowCompleted(checked);
                setMeetings([]);
                setPage(1);
                setHasMore(true);
              }}
            />
            <Label htmlFor="show-completed">Show completed</Label>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {meetings.map((meeting) => (
          <div key={meeting.id} className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <h2
                className="text-lg font-semibold cursor-pointer hover:text-primary break-words flex-1"
                onClick={() => navigate(`/app/meetings/${meeting.id}`)}
              >
                {`${meeting.title} - ${format(new Date(meeting.date), 'dd/MM/yyyy')}`}
              </h2>
              <CollaborationIndicator 
                owner={typeof meeting.owner === 'object' && meeting.owner?.email ? meeting.owner : { email: 'unknown@user.com' }}
                currentUserEmail={currentUserEmail}
              />
            </div>
            <div className="space-y-3">
              {meeting.actions.map((action) => (
                <div
                  key={action.id}
                  className="group flex items-start gap-4 p-4 rounded-xl glass-panel cursor-pointer transition-all duration-300 hover:shadow-glass-hover hover:-translate-y-0.5"
                >
                  <Checkbox
                    checked={action.completed}
                    onCheckedChange={(checked) => 
                      handleActionToggle(meeting.id, action.id, checked as boolean)
                    }
                    className="mt-1 rounded border-primary"
                  />
                  <div className={`flex-1 break-words transition-colors ${action.completed ? 'line-through text-muted-foreground' : 'group-hover:text-fluid-primary dark:group-hover:text-indigo-400'}`}>
                    {renderHashtagText(action.text, action.hashtags)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {isLoading && <LoadingSpinner />}
        {!hasMore && meetings.length > 0 && (
          <p className="text-center text-muted-foreground py-4">
            No more actions to load.
          </p>
        )}
        <div ref={observerRef} />
      </div>
    </div>
  );
}
